import { fn } from '@ember/helper';
import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { debounce, later, next } from '@ember/runloop';
import { service } from '@ember/service';

import type { Service } from 'webapp-types';
import type { NewReportSlideLayout } from 'webapp/enums/enums';
import { PopupContextSwitchAction as ContextSwitchAction } from 'webapp/enums/enums';
import { TrackingEvent } from 'webapp/enums/tracking';

import eq from 'webapp/helpers/eq';
import didUpdate from 'webapp/modifiers/did-update';
import type { Args as BaseArgs } from 'webapp/components/reports/editor-base';
import ReportsEditorBaseComponent, { OrderProperty } from 'webapp/components/reports/editor-base';
import ReportsEditorBlockSettingsComponent from 'webapp/components/reports/editor/block-settings';
import ReportsEditorDescriptionComponent from 'webapp/components/reports/editor/description';
import ReportsEditorEmptyComponent from 'webapp/components/reports/editor/empty';
import ReportsEditorFragmentBlockComponent from 'webapp/components/reports/editor/fragment/block';
import ReportsEditorFragmentBoardComponent from 'webapp/components/reports/editor/fragment/board';
import ReportsEditorFragmentImageComponent from 'webapp/components/reports/editor/fragment/image';
import ReportsEditorFragmentPlaceholderComponent from 'webapp/components/reports/editor/fragment/placeholder';
import ReportsEditorFragmentStoryTextComponent from 'webapp/components/reports/editor/fragment/story-text';
import ReportsEditorFragmentTitleComponent from 'webapp/components/reports/editor/fragment/title';
import ReportsEditorHeaderComponent from 'webapp/components/reports/editor/header';
import ReportsEditorNavigationComponent from 'webapp/components/reports/editor/navigation';
import ReportsEditorSlideOuterComponent from 'webapp/components/reports/editor/slide-outer';
import ReportsEditorSlideWrapperComponent from 'webapp/components/reports/editor/slide-wrapper';
import ReportsFragmentGridComponent from 'webapp/components/reports/fragment-grid/index';
import ReportsPopupDuplicateReportSlideComponent from 'webapp/components/reports/popup-duplicate-report-slide';

import type ReportSlide from 'webapp/models-custom/report-slide';
import type ReportSlideRecord from 'webapp/models/report-slide';

interface Args extends BaseArgs {
	selectedSlideId: string | null;
	onSlideChange: (slideId: string | null) => void;
}

export default class ReportsEditorSlidesComponent extends ReportsEditorBaseComponent<Args> {
	@service('analytics') declare analyticsService: Service.Analytics;
	@service('tour') declare tourService: Service.Tour;
	@service('flow/tour/reports-slides') declare slidesTourService: Service.Flow.Tour.ReportsSlides;

	orderProperty = OrderProperty.ORDER;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		next(() => {
			if (!this.selectedSlide) {
				this.openFirstSlide();
			}

			// start slides tour when switching to slides view
			later(() => {
				this.slidesTourService.checkTour();
			}, 1000);
		});
	}

	get selectedSlide() {
		return this.args.model.slides.find((item) => item.id === this.args.selectedSlideId);
	}

	get selectedSlideId() {
		return this.selectedSlide?.id;
	}

	get shownFragments() {
		return this.selectedSlide?.fragments || [];
	}

	override get isFragmentGridEditable() {
		return !this.selectedSlide?.isTypeBoard;
	}

	saveSlide(slide: ReportSlide): void {
		// slide (description, visual settings, etc) is saved here
		// slide fragments are saved in their respective type components
		slide.save().catch(this.requestService.showAllValidationErrors.bind(this.requestService));
		this.mmReportsService.triggerReportEdited();
	}

	@action
	addNewSlide(fromLayout: NewReportSlideLayout): Promise<ReportSlideRecord> {
		return this.mmReportSlidesService
			.createNewFromLayout(this.args.model, fromLayout)
			.then((reportSlideRecord) => {
				this.openLastSlide();
				return reportSlideRecord;
			});
	}

	@action
	openLastSlide(): void {
		const slides = this.args.model.sortedSlides,
			lastSlide = slides[slides.length - 1];
		if (lastSlide) {
			this.openSlide(lastSlide);
		}
	}

	@action
	openFirstSlide(): void {
		const firstSlide = this.args.model.slides[0];
		if (firstSlide) {
			this.openSlide(firstSlide);
		}
	}

	@action
	openSlide(slide: ReportSlide): void {
		this.args.onSlideChange(slide?.id || null);

		if (
			this.blockFragmentToEdit &&
			this.blockFragmentToEdit?.reportSlideId !== slide?.numericId
		) {
			this.changeBlockFragmentToEdit(null);
		}
	}

	@action
	updateSlide(slide: ReportSlide): void {
		debounce(this, this.saveSlide, slide, 200);
	}

	@action
	duplicateSlide(slide: ReportSlide): void {
		const duplicateSlidePopup = this.popupService.createModal(
			ReportsPopupDuplicateReportSlideComponent,
			{ popupClass: 'popup-type-duplicate-block title-bigger' },
		);
		duplicateSlidePopup.open({
			reportSlide: slide,
			onDuplicate: (slideRecord: ReportSlideRecord) => {
				if (slide.reportId === slideRecord.reportId) {
					const reorderedSlides = this.mmReportsService.reorderSlides(this.args.model),
						slideFromReportSlides = reorderedSlides.find(
							(item) => item.id === slideRecord.id,
						);
					if (slideFromReportSlides) {
						this.openSlide(slideFromReportSlides);
					}
				} else {
					const reportSlideModel = this.mmReportSlidesService.findModel(slideRecord.id!),
						targetReportModel = this.mmReportsService.findModel(
							String(slideRecord.reportId),
						);

					this.navigationService.createContextSwitchModal().open({
						actionType: ContextSwitchAction.ADD_TO,
						sourceItem: reportSlideModel!,
						targetItem: targetReportModel!,
						onSwitch: () => {
							this.navigationService.transitionToRoute(
								'reports.editor.report',
								slideRecord.reportId,
							);
						},
					});
				}
			},
		});
	}

	@action
	removeSlide(slide: ReportSlide): void {
		const affirmativeFunc = () => {
			const allSlides = this.args.model.sortedSlides,
				slideToDelete = allSlides.find((item) => item.id === slide.id)!,
				prevSlide = allSlides[allSlides.indexOf(slideToDelete) - 1],
				nextSlide = allSlides[allSlides.indexOf(slideToDelete) + 1];

			const slideToOpen = nextSlide || prevSlide;
			if (slideToOpen) {
				this.openSlide(slideToOpen);
			}

			if (slide.isTypeBoard) {
				// if board on slide was deleted, the board can be undefined
				const board = slide.board,
					sameBoardSlides = this.args.model.slides.filter(
						(slide) => slide.board?.id === board?.id,
					),
					skipClearConnectedRecords = sameBoardSlides.length > 1;
				void slide.delete(false, skipClearConnectedRecords);
			} else {
				slide
					.delete()
					.catch(this.requestService.handleGenericThrownError.bind(this.requestService));
			}

			this.mmReportsService.triggerReportEdited();
		};

		this.popupService.openConfirmPopup({
			onConfirm: affirmativeFunc,
			title: 'Do you want to remove this slide from the report?',
			text: '',
			confirmAction: 'Delete',
			cancelAction: 'Cancel',
		});
	}

	@action
	createReportAndAddSlide(requestedType: NewReportSlideLayout): Promise<ReportSlideRecord> {
		// if empty report is switched from pageless isNew is false but it was still not created yet
		if (this.args.model.isNew || !this.selectedSlide) {
			return this.args.model
				.save()
				.then((reportRecord) => {
					const origin =
						this.mmReportsService.getAndClearReportCreationOrigin() || 'page';
					this.analyticsService.event(TrackingEvent.REPORT_CREATED, { origin });

					return this.navigationService
						.replaceRoute(`/reports/editor/${reportRecord.id}`)
						.then(() => this.addNewSlide(requestedType));
				})
				.catch(
					this.requestService.showAllValidationErrors.bind(this.requestService),
				) as Promise<ReportSlideRecord>;
		} else {
			return this.addNewSlide(requestedType);
		}
	}

	override willDestroy(): void {
		super.willDestroy();

		// if selected slide is cover slide, and there is only one slide, then cleanup empty story text fragments
		const selectedSlide = this.selectedSlide;
		if (this.args.model.slides.length === 1 && selectedSlide?.isTypeCover) {
			const emptyStoryTextFragments = selectedSlide.fragments.filter(
				(fragment) => fragment.isTypeStoryText && fragment.isEmpty,
			);
			emptyStoryTextFragments.forEach((fragment) => {
				void fragment.deleteAndCatch();
			});
		}
	}
	<template>
		<ReportsEditorHeaderComponent
			@model={{@model}}
			@changeViewMode={{@changeViewMode}}
			@closeEditor={{@closeEditor}}
		/>

		<div
			id="report-editor_body"
			class="report_ct report_ct--slides"
			{{didUpdate this.openFirstSlide @model}}
		>
			<ReportsEditorNavigationComponent
				@model={{@model}}
				@selectedSlide={{this.selectedSlide}}
				@onAddNewSlide={{this.createReportAndAddSlide}}
				@onOpenSlide={{this.openSlide}}
				@onRemoveSlide={{this.removeSlide}}
			/>

			<div
				class="report_slide-and-desc_ct
					{{if this.isBlockSettingsOpen 'block-settings-open'}}"
			>

				{{#if this.selectedSlide}}

					<ReportsEditorDescriptionComponent
						@model={{this.selectedSlide}}
						@onUpdateSlide={{this.updateSlide}}
					/>

					<ReportsEditorSlideOuterComponent
						@model={{this.selectedSlide}}
						@onUpdateSlide={{this.updateSlide}}
						@onDuplicateSlide={{this.duplicateSlide}}
						@onRemoveSlide={{this.removeSlide}}
					>
						<ReportsEditorSlideWrapperComponent
							@model={{this.selectedSlide}}
							@onFragmentChange={{this.onFragmentChange}}
							as |slideOnFragmentChange|
						>
							<ReportsFragmentGridComponent
								@model={{this.shownFragments}}
								@report={{@model}}
								@reportSlide={{this.selectedSlide}}
								@isEditable={{this.isFragmentGridEditable}}
								@orderProperty={{this.orderProperty}}
								@onFragmentChange={{slideOnFragmentChange}}
								as |fragments Item onFragmentChange|
							>
								{{#each fragments as |fragment|}}
									<Item @model={{fragment}}>
										{{#if fragment.isTypeBoard}}
											<ReportsEditorFragmentBoardComponent
												@model={{fragment}}
											/>
										{{else if fragment.isTypeImage}}
											<ReportsEditorFragmentImageComponent
												@model={{fragment}}
												@onFragmentChange={{onFragmentChange}}
											/>
										{{else if fragment.isTypeBlock}}
											<ReportsEditorFragmentBlockComponent
												@model={{fragment}}
												@onFragmentChange={{onFragmentChange}}
												@changeBlockFragmentToEdit={{this.changeBlockFragmentToEdit}}
												@isBeingEdited={{eq
													fragment.id
													this.blockFragmentToEdit.id
												}}
											/>
										{{else if fragment.isTypeStoryText}}
											<ReportsEditorFragmentStoryTextComponent
												@model={{fragment}}
												@onFragmentChange={{onFragmentChange}}
												@isBeingEdited={{eq
													fragment.id
													this.blockFragmentToEdit.id
												}}
											/>
										{{else if fragment.isTypeTitle}}
											<ReportsEditorFragmentTitleComponent
												@model={{fragment}}
												@onFragmentChange={{onFragmentChange}}
											/>
										{{else if fragment.isTypePlaceholder}}
											<ReportsEditorFragmentPlaceholderComponent
												@model={{fragment}}
												@onFragmentChange={{onFragmentChange}}
											/>
										{{/if}}
									</Item>
								{{/each}}
							</ReportsFragmentGridComponent>
						</ReportsEditorSlideWrapperComponent>
					</ReportsEditorSlideOuterComponent>

					<ReportsEditorBlockSettingsComponent
						@fragment={{this.blockFragmentToEdit}}
						@onClose={{fn this.changeBlockFragmentToEdit null}}
					/>

				{{else}}
					<ReportsEditorEmptyComponent
						@onAddNewSlide={{this.createReportAndAddSlide}}
						@addNewSlideLayoutsDisabled={{@model.addNewSlideLayoutsDisabled}}
					/>

				{{/if}}

			</div>
		</div>
	</template>
}
